import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { AiOutlineGroup } from 'react-icons/ai';
import { CgTemplate } from 'react-icons/cg';
import { TbAsset } from 'react-icons/tb';
import { AssetNodeType, TemplateTypeLabel } from '../models';
import AssetTreeNode from './AssetTreeNode';
import {
  getGroupDisplayName,
  getItemDisplayName,
  hasUnnamedGroup,
  hasUnnamedItem,
} from '../helpers';

interface AssetTreeProps {
  templates: any[];
  groups: any[];
  items: any[];
  tree: any;
  selectedNodeId: string;
  expandedNodeIds: string[];
  searchTerm: string;
  selectedTemplateId: number | null;
  selectedSidebarItem: { itemType: string; id: number } | null;
  onSelectNode: (nodeId: string) => void;
  onToggleNode: (nodeId: string) => void;
  onSearchChange: (term: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onAddTemplate: () => void;
  onSelectTemplate: (templateId: number) => void;
  onSelectGroup: (group: any) => void;
  onSelectItem: (item: any) => void;
  onBackToTemplates: () => void;
  onLinkDrop: (
    targetType: 'template' | 'group',
    targetId: number,
    dragType: 'group' | 'item',
    dragId: number
  ) => void;
}

type TreeDropHandlers = {
  dropTargetKey: string | null;
  onDragOver: (e: React.DragEvent, targetType: 'template' | 'group', targetId: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, targetType: 'template' | 'group', targetId: number) => void;
};

const renderNodes = (
  node: any,
  depth: number,
  selectedNodeId: string,
  expandedNodeIds: string[],
  onToggle: (id: string) => void,
  onSelect: (id: string) => void,
  dnd: TreeDropHandlers
): React.ReactNode[] => {
  if (!node || !node.id) return [];
  const isExpanded = expandedNodeIds.includes(node.id);
  const isSelected = node.id === selectedNodeId;
  const elements: React.ReactNode[] = [];

  const dropType: 'template' | 'group' | null =
    node.type === AssetNodeType.Template
      ? 'template'
      : node.type === AssetNodeType.Group
      ? 'group'
      : null;
  const dropId = Number(node.rawId);
  const acceptsDrop = dropType != null && Number.isFinite(dropId);

  elements.push(
    <AssetTreeNode
      key={node.id}
      node={node}
      depth={depth}
      isExpanded={isExpanded}
      isSelected={isSelected}
      isDropTarget={acceptsDrop && dnd.dropTargetKey === `${dropType}-${dropId}`}
      onToggle={onToggle}
      onSelect={onSelect}
      onDragOver={
        acceptsDrop ? (e: React.DragEvent) => dnd.onDragOver(e, dropType, dropId) : undefined
      }
      onDragLeave={acceptsDrop ? dnd.onDragLeave : undefined}
      onDrop={acceptsDrop ? (e: React.DragEvent) => dnd.onDrop(e, dropType, dropId) : undefined}
    />
  );

  if (isExpanded && node.children) {
    node.children.forEach((child: any) => {
      elements.push(
        ...renderNodes(child, depth + 1, selectedNodeId, expandedNodeIds, onToggle, onSelect, dnd)
      );
    });
  }

  return elements;
};

type SectionKey = 'componentTree' | 'templates' | 'groups' | 'assets';

const AssetTree: React.FC<AssetTreeProps> = ({
  templates,
  groups,
  items,
  tree,
  selectedNodeId,
  expandedNodeIds,
  searchTerm,
  selectedTemplateId,
  selectedSidebarItem,
  onSelectNode,
  onToggleNode,
  onSearchChange,
  onExpandAll,
  onCollapseAll,
  onAddTemplate,
  onSelectTemplate,
  onSelectGroup,
  onSelectItem,
  onBackToTemplates,
  onLinkDrop,
}) => {
  const intl = useIntl();
  const allExpanded = expandedNodeIds.length > 0;
  const isTemplateSelected = selectedTemplateId != null && tree != null;
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    componentTree: false,
    templates: false,
    groups: false,
    assets: false,
  });
  const isSectionCollapsed = (key: SectionKey) => collapsedSections[key];
  const toggleSection = (key: SectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDragStart = (e: React.DragEvent, type: 'group' | 'item', id: number) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type, id }));
    e.dataTransfer.effectAllowed = 'copy';
    const el = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
    });
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
  };

  const handleDragOver = (e: React.DragEvent, targetType: string, targetId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetKey(`${targetType}-${targetId}`);
  };

  const handleDragLeave = () => {
    setDropTargetKey(null);
  };

  const handleDrop = (e: React.DragEvent, targetType: 'template' | 'group', targetId: number) => {
    e.preventDefault();
    setDropTargetKey(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type && data.id) {
        onLinkDrop(targetType, targetId, data.type, data.id);
      }
    } catch {
      // noop
    }
  };

  const otherTemplates = (templates || []).filter(
    (t: any) => !isTemplateSelected || t.id !== selectedTemplateId
  );

  return (
    <div className="asset-tree">
      <div className="asset-tree__header">
        <span className="asset-tree__title">{intl.formatMessage({ id: 'assets.tree.title' })}</span>
        {isTemplateSelected && (
          <button
            className="asset-tree__expand-btn"
            onClick={allExpanded ? onCollapseAll : onExpandAll}
          >
            {allExpanded
              ? intl.formatMessage({ id: 'assets.tree.collapseAll' })
              : intl.formatMessage({ id: 'assets.tree.expandAll' })}
          </button>
        )}
      </div>

      <div className="asset-tree__search">
        <i className="uil uil-search asset-tree__search-icon" />
        <input
          type="text"
          className="asset-tree__search-input"
          placeholder={intl.formatMessage({ id: 'assets.tree.search' })}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="asset-tree__nodes" role="tree">
        {isTemplateSelected && tree && (
          <>
            <div className="asset-tree__section-label asset-tree__section-label--with-action">
              <button
                type="button"
                className="asset-tree__section-toggle"
                onClick={() => toggleSection('componentTree')}
                aria-expanded={!isSectionCollapsed('componentTree')}
              >
                <i
                  className={`uil ${
                    isSectionCollapsed('componentTree') ? 'uil-angle-right' : 'uil-angle-down'
                  } asset-tree__section-chevron`}
                />
                <span>{intl.formatMessage({ id: 'assets.tree.componentTree' })}</span>
              </button>
              <button
                className="asset-tree__back-btn asset-tree__back-btn--inline"
                onClick={onBackToTemplates}
                title="Close template"
              >
                <i className="uil uil-times" />
              </button>
            </div>
            {!isSectionCollapsed('componentTree') &&
              renderNodes(tree, 0, selectedNodeId, expandedNodeIds, onToggleNode, onSelectNode, {
                dropTargetKey,
                onDragOver: handleDragOver,
                onDragLeave: handleDragLeave,
                onDrop: handleDrop,
              })}
          </>
        )}

        <div className="asset-tree__section-label">
          <button
            type="button"
            className="asset-tree__section-toggle"
            onClick={() => toggleSection('templates')}
            aria-expanded={!isSectionCollapsed('templates')}
          >
            <i
              className={`uil ${
                isSectionCollapsed('templates') ? 'uil-angle-right' : 'uil-angle-down'
              } asset-tree__section-chevron`}
            />
            <span>{intl.formatMessage({ id: 'assets.tree.templates' })}</span>
          </button>
        </div>
        {!isSectionCollapsed('templates') &&
          otherTemplates.map((template: any) => (
            <div
              key={`tpl-${template.id}`}
              className={`asset-tree-node${
                dropTargetKey === `template-${template.id}` ? ' asset-tree-node--drop-target' : ''
              }`}
              onClick={() => onSelectTemplate(template.id)}
              onDragOver={(e) => handleDragOver(e, 'template', template.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'template', template.id)}
              style={{ padding: '8px 12px', cursor: 'pointer' }}
            >
              <CgTemplate className="asset-tree-node__icon" size={16} color="#6c757d" />
              <div className="asset-tree-node__content">
                <span className="asset-tree-node__name">{template.name}</span>
                <div className="asset-tree-node__meta">
                  <span
                    className="asset-tree-node__type-badge"
                    style={{ background: '#f6f6f8', color: '#6c757d' }}
                  >
                    {TemplateTypeLabel[template.type] || template.type}
                  </span>
                  {template.version && (
                    <span className="asset-tree-node__counts">{template.version}</span>
                  )}
                </div>
              </div>
            </div>
          ))}

        <div className="asset-tree__section-label">
          <button
            type="button"
            className="asset-tree__section-toggle"
            onClick={() => toggleSection('groups')}
            aria-expanded={!isSectionCollapsed('groups')}
          >
            <i
              className={`uil ${
                isSectionCollapsed('groups') ? 'uil-angle-right' : 'uil-angle-down'
              } asset-tree__section-chevron`}
            />
            <span>{intl.formatMessage({ id: 'assets.tree.groups' })}</span>
          </button>
        </div>
        {!isSectionCollapsed('groups') &&
          (groups || []).map((group: any) => {
            const isGroupSelected =
              selectedSidebarItem?.itemType === 'group' && selectedSidebarItem?.id === group.id;
            const isGroupDropTarget = dropTargetKey === `group-${group.id}`;
            return (
              <div
                key={`grp-${group.id}`}
                className={`asset-tree-node${isGroupSelected ? ' asset-tree-node--selected' : ''}${
                  isGroupDropTarget ? ' asset-tree-node--drop-target' : ''
                }`}
                draggable
                onClick={() => onSelectGroup(group)}
                onDragStart={(e) => handleDragStart(e, 'group', group.id)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, 'group', group.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 'group', group.id)}
                style={{ padding: '8px 12px', cursor: 'pointer' }}
              >
                <AiOutlineGroup className="asset-tree-node__icon" size={16} color="#6c757d" />
                <div className="asset-tree-node__content">
                  <span
                    className={`asset-tree-node__name${
                      hasUnnamedGroup(group) ? ' asset-tree-node__name--unnamed' : ''
                    }`}
                  >
                    {getGroupDisplayName(group)}
                  </span>
                  <div className="asset-tree-node__meta">
                    <span
                      className="asset-tree-node__type-badge"
                      style={{ background: '#f6f6f8', color: '#6c757d' }}
                    >
                      Group
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

        <div className="asset-tree__section-label">
          <button
            type="button"
            className="asset-tree__section-toggle"
            onClick={() => toggleSection('assets')}
            aria-expanded={!isSectionCollapsed('assets')}
          >
            <i
              className={`uil ${
                isSectionCollapsed('assets') ? 'uil-angle-right' : 'uil-angle-down'
              } asset-tree__section-chevron`}
            />
            <span>{intl.formatMessage({ id: 'assets.tree.assets' })}</span>
          </button>
        </div>
        {!isSectionCollapsed('assets') &&
          (items || []).map((item: any) => {
            const isItemSelected =
              selectedSidebarItem?.itemType === 'item' && selectedSidebarItem?.id === item.id;
            return (
              <div
                key={`item-${item.id}`}
                className={`asset-tree-node${isItemSelected ? ' asset-tree-node--selected' : ''}`}
                draggable
                onClick={() => onSelectItem(item)}
                onDragStart={(e) => handleDragStart(e, 'item', item.id)}
                onDragEnd={handleDragEnd}
                style={{ padding: '8px 12px', cursor: 'grab' }}
              >
                <TbAsset className="asset-tree-node__icon" size={16} color="#6c757d" />
                <div className="asset-tree-node__content">
                  <span
                    className={`asset-tree-node__name${
                      hasUnnamedItem(item) ? ' asset-tree-node__name--unnamed' : ''
                    }`}
                  >
                    {getItemDisplayName(item)}
                  </span>
                  <div className="asset-tree-node__meta">
                    <span
                      className="asset-tree-node__type-badge"
                      style={{ background: '#f6f6f8', color: '#6c757d' }}
                    >
                      Item
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <button className="asset-tree__add-template" onClick={onAddTemplate}>
        <i className="uil uil-plus" />
        {intl.formatMessage({ id: 'assets.tree.addTemplate' })}
      </button>
    </div>
  );
};

export default AssetTree;
